import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Search,
  ChevronsLeft,
  ChevronsRight,
  Download,
  X,
  GitMerge,
} from "lucide-react";
import { useAppDialog } from "../components/AppDialog";
import AdminShell from "../components/admin/AdminShell";

const BASE = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const COLUMNS = [
  { key: "visitor_id", label: "Visitor ID", sortable: false },
  { key: "last_name", label: "Name", sortable: true },
  { key: "google_email", label: "Linked email", sortable: false },
  { key: "dob", label: "DOB", sortable: false },
  { key: "country", label: "Location", sortable: false },
  { key: "device", label: "Device", sortable: false },
  { key: "first_seen", label: "First seen", sortable: true },
  { key: "last_seen", label: "Last seen", sortable: true },
  { key: "visit_count", label: "Visits", sortable: true, align: "right" },
  { key: "total_plays", label: "Plays", sortable: true, align: "right" },
  { key: "playtime_seconds", label: "Playtime", sortable: true, align: "right" },
  { key: "coins_earned", label: "Coins", sortable: true, align: "right" },
  { key: "referrals", label: "Refs", sortable: true, align: "right" },
];

const AdminUsers = () => {
  const nav = useNavigate();
  const { prompt, confirm } = useAppDialog();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sort, setSort] = useState("last_seen");
  const [order, setOrder] = useState("desc");
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [country, setCountry] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [mergeResult, setMergeResult] = useState(null);

  // Debounce search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  // Populate the country dropdown from /admin/stats (already cached upstream).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/api/admin/stats`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setCountries((d.countries || []).map((c) => c.key));
      } catch {
        /* ignore — filter still works via free-text */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set("sort", sort);
    params.set("order", order);
    if (debouncedQ) params.set("q", debouncedQ);
    if (country) params.set("country", country);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return params;
  }, [sort, order, debouncedQ, country, dateFrom, dateTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = buildParams();
      params.set("skip", skip);
      params.set("limit", limit);
      const url = `${BASE}/api/admin/users?${params.toString()}`;
      const r = await fetch(url, { credentials: "include" });
      if (r.status === 401 || r.status === 403) {
        nav("/admin", { replace: true });
        return;
      }
      if (!r.ok) {
        setError("Failed to load users.");
        setLoading(false);
        return;
      }
      const data = await r.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [buildParams, skip, limit, nav]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Reset pagination when any filter changes.
    setSkip(0);
  }, [debouncedQ, sort, order, country, dateFrom, dateTo]);

  const setSortKey = (k) => {
    if (!COLUMNS.find((c) => c.key === k)?.sortable) return;
    if (sort === k) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else {
      setSort(k);
      setOrder("desc");
    }
  };

  const clearFilters = () => {
    setQ("");
    setCountry("");
    setDateFrom("");
    setDateTo("");
  };

  const downloadCsv = () => {
    const params = buildParams();
    params.set("limit", "5000");
    // /admin/users.csv always exports from row 0 — no skip param needed.
    const url = `${BASE}/api/admin/users.csv?${params.toString()}`;
    // Open in a new tab so the browser handles the download; credentials via cookie.
    window.open(url, "_blank", "noopener");
  };

  const hasFilters = Boolean(q || country || dateFrom || dateTo);
  const page = Math.floor(skip / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  // --- Selection + merge --------------------------------------------------
  const toggleSelect = (vid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid);
      else next.add(vid);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = rows.every((r) => next.has(r.visitor_id));
      rows.forEach((r) => {
        if (allSelected) next.delete(r.visitor_id);
        else next.add(r.visitor_id);
      });
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const mergeSelected = async () => {
    // Show the picker list in TABLE row order (not Set insertion order) so
    // the numbers in the prompt() line up with what the admin sees.
    const selectedRows = rows.filter((r) => selected.has(r.visitor_id));
    const ids = selectedRows.map((r) => r.visitor_id);
    if (ids.length < 2) return;
    // Default canonical = the currently-selected row with a linked email +
    // longest playtime + earliest first_seen — same as the backend heuristic
    // but the admin can pick a different one via prompt.
    const scored = [...selectedRows].sort((a, b) => {
      const ae = a.google_email ? 0 : 1;
      const be = b.google_email ? 0 : 1;
      if (ae !== be) return ae - be;
      const ap = -(a.playtime_seconds || 0);
      const bp = -(b.playtime_seconds || 0);
      if (ap !== bp) return ap - bp;
      return (a.first_seen || "9999") < (b.first_seen || "9999") ? -1 : 1;
    });
    const suggested = scored[0]?.visitor_id;
    const namesById = Object.fromEntries(
      selectedRows.map((r) => [r.visitor_id, r.last_name || r.first_name || r.visitor_id.slice(0, 10)])
    );
    const list = ids
      .map((id, i) => `${i + 1}. ${namesById[id]} (${id.slice(0, 12)}…)${id === suggested ? "  ← suggested keep" : ""}`)
      .join("\n");
    const answer = await prompt({
      title: "Which account should we KEEP?",
      message: `Merging ${ids.length} accounts into one. Enter 1-${ids.length}, or leave the suggested pick.\n\n${list}`,
      defaultValue: String(ids.indexOf(suggested) + 1),
      placeholder: `1-${ids.length}`,
      confirmLabel: "Continue",
    });
    if (answer === null || answer === undefined) return;
    let canonical = suggested;
    if (answer.trim()) {
      const idx = parseInt(answer, 10);
      if (!Number.isFinite(idx) || idx < 1 || idx > ids.length) {
        setMergeError("Invalid pick — merge cancelled.");
        return;
      }
      canonical = ids[idx - 1];
    }
    const ok = await confirm({
      title: `Merge into "${namesById[canonical]}"?`,
      message: `${ids.length - 1} account${ids.length - 1 === 1 ? "" : "s"} will be folded into this one. This cannot be undone.`,
      tone: "danger",
      confirmLabel: "Yes, merge",
    });
    if (!ok) return;
    setMerging(true);
    setMergeError("");
    setMergeResult(null);
    try {
      const r = await fetch(`${BASE}/api/admin/merge-selected`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visitor_ids: ids, canonical_visitor_id: canonical }),
      });
      if (!r.ok) {
        const detail = await r.json().catch(() => null);
        setMergeError((detail && detail.detail) || `Merge failed (HTTP ${r.status}).`);
      } else {
        const data = await r.json();
        setMergeResult(data.result || null);
        clearSelection();
        await load();
      }
    } catch {
      setMergeError("Network error while merging.");
    } finally {
      setMerging(false);
    }
  };

  return (
    <AdminShell
      right={
        <span className="text-sm" data-testid="admin-users-total" style={{ color: "#fff" }}>
          {total.toLocaleString()} total
        </span>
      }
    >
      <div data-testid="admin-users">
        <div
          className="hidden md:flex items-center justify-between gap-3 mb-4 sticky top-0 z-20 py-3 border-b-4"
          style={{ background: "#FFF9E6", borderColor: "#2b2b2b" }}
        >
          <h1 className="font-display text-2xl sm:text-3xl px-4 sm:px-6">
            All Users
          </h1>
          <span className="text-sm px-4 sm:px-6" data-testid="admin-users-total-desktop">
            {total.toLocaleString()} total
          </span>
        </div>

        <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        <div className="chunky-card p-3 sm:p-4 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div
              className="flex items-center gap-2 flex-1 min-w-[220px]"
              style={{
                border: "3px solid #2b2b2b",
                borderRadius: 12,
                padding: "6px 10px",
                background: "#FFF9E6",
              }}
            >
              <Search size={18} strokeWidth={3} />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search visitor id or name…"
                data-testid="admin-users-search"
                className="w-full outline-none bg-transparent font-display text-base"
                style={{ minHeight: 32 }}
              />
            </div>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="admin-users-country"
              className="font-display text-sm px-3 py-2"
              style={{
                border: "3px solid #2b2b2b",
                borderRadius: 12,
                background: "#EFE7FF",
                minWidth: 140,
              }}
            >
              <option value="">All countries</option>
              {countries.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <label className="text-xs" style={{ color: "#484848" }}>
                First seen
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="admin-users-date-from"
                className="font-display text-sm px-2 py-2"
                style={{
                  border: "3px solid #2b2b2b",
                  borderRadius: 12,
                  background: "#E5F6FF",
                }}
              />
              <span style={{ color: "#484848" }}>→</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="admin-users-date-to"
                className="font-display text-sm px-2 py-2"
                style={{
                  border: "3px solid #2b2b2b",
                  borderRadius: 12,
                  background: "#E5F6FF",
                }}
              />
            </div>
            {hasFilters && (
              <button
                type="button"
                onClick={clearFilters}
                data-testid="admin-users-clear"
                className="btn-3d btn-sm"
                aria-label="Clear filters"
              >
                <X size={14} strokeWidth={3} />
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="admin-users-csv"
              className="btn-3d btn-sm btn-purple"
            >
              <Download size={14} strokeWidth={3} />
              CSV
            </button>
          </div>
          <div
            className="text-sm mt-3"
            style={{ color: "#484848" }}
            data-testid="admin-users-count"
          >
            Showing {rows.length} of {total.toLocaleString()}
            {hasFilters ? " · filtered" : ""}
          </div>
        </div>

        {selected.size > 0 && (
          <div
            className="chunky-card p-3 mb-3 flex items-center gap-3 flex-wrap"
            style={{ background: "#EFE7FF" }}
            data-testid="admin-users-selection-bar"
          >
            <span className="font-display">
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={mergeSelected}
              disabled={selected.size < 2 || merging}
              data-testid="admin-users-merge-selected"
              className="btn-3d btn-sm btn-purple"
            >
              <GitMerge size={14} strokeWidth={3} />
              {merging
                ? "Merging…"
                : selected.size < 2
                ? "Merge selected"
                : `Merge ${selected.size} into one`}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              data-testid="admin-users-selection-clear"
              className="btn-3d btn-sm"
            >
              <X size={14} strokeWidth={3} />
              Clear
            </button>
            {selected.size < 2 && (
              <span className="text-xs" style={{ color: "#8a8a8a" }}>
                Pick at least 2 rows to merge.
              </span>
            )}
          </div>
        )}

        {mergeError && (
          <div
            className="chunky-card p-3 mb-3"
            style={{ background: "#FFE9E9", color: "#B00020" }}
            data-testid="admin-users-merge-error"
          >
            {mergeError}
          </div>
        )}

        {mergeResult && (() => {
          const mergedCount = (mergeResult.merged_visitor_ids || []).length;
          const eventsCount = mergeResult.events_reassigned || 0;
          const coinsCount = mergeResult.coins_moved || 0;
          return (
            <div
              className="chunky-card p-3 mb-3"
              style={{ background: "#E9FFE6", color: "#136B22" }}
              data-testid="admin-users-merge-result"
            >
              ✅ Merged {mergedCount} account{mergedCount === 1 ? "" : "s"} into{" "}
              <span className="font-mono">
                {String(mergeResult.canonical_visitor_id).slice(0, 12)}…
              </span>{" "}
              · {coinsCount} coin{coinsCount === 1 ? "" : "s"} moved ·{" "}
              {eventsCount} event{eventsCount === 1 ? "" : "s"} reassigned.
            </div>
          );
        })()}

        {error && (
          <div
            className="chunky-card p-3 mb-3"
            style={{ background: "#FF5A5F22", color: "#FF5A5F" }}
            data-testid="admin-users-error"
          >
            {error}
          </div>
        )}

        <div className="chunky-card p-0 overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead style={{ background: "#FFB400" }}>
                <tr style={{ borderBottom: "3px solid #2b2b2b" }}>
                  <th className="py-2 px-2 text-center" style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      onChange={toggleSelectAllVisible}
                      checked={
                        rows.length > 0 &&
                        rows.every((r) => selected.has(r.visitor_id))
                      }
                      aria-label="Select all visible"
                      data-testid="admin-users-select-all"
                    />
                  </th>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      className={`py-2 px-3 text-${c.align || "left"} whitespace-nowrap ${
                        c.sortable ? "cursor-pointer select-none" : ""
                      }`}
                      onClick={() => setSortKey(c.key)}
                      data-testid={`admin-users-col-${c.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && sort === c.key && (
                          order === "asc"
                            ? <ChevronUp size={14} strokeWidth={3} />
                            : <ChevronDown size={14} strokeWidth={3} />
                        )}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="py-6 text-center" data-testid="admin-users-loading">
                      Loading…
                    </td>
                  </tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} className="py-6 text-center" style={{ color: "#484848" }} data-testid="admin-users-empty">
                      No visitors match.
                    </td>
                  </tr>
                )}
                {!loading && rows.map((v) => (
                  <tr
                    key={v.visitor_id}
                    style={{
                      borderBottom: "1px solid #eee",
                      background: selected.has(v.visitor_id) ? "#EFE7FF" : undefined,
                    }}
                    data-testid={`admin-users-row-${v.visitor_id}`}
                  >
                    <td className="py-2 px-2 text-center">
                      <input
                        type="checkbox"
                        checked={selected.has(v.visitor_id)}
                        onChange={() => toggleSelect(v.visitor_id)}
                        aria-label={`Select ${v.visitor_id}`}
                        data-testid={`admin-users-select-${v.visitor_id}`}
                      />
                    </td>
                    <td className="py-2 px-3 font-mono text-xs">
                      <Link
                        to={`/admin/users/${v.visitor_id}`}
                        style={{ color: "#5F27CD", textDecoration: "underline" }}
                        data-testid={`admin-users-link-${v.visitor_id}`}
                      >
                        {v.visitor_id.slice(0, 16)}
                        {v.visitor_id.length > 16 ? "…" : ""}
                      </Link>
                    </td>
                    <td className="py-2 px-3">{v.last_name || v.first_name || "—"}</td>
                    <td
                      className="py-2 px-3 text-xs"
                      data-testid={`admin-users-email-${v.visitor_id}`}
                      style={{ color: v.google_email ? "#2b2b2b" : "#8a8a8a" }}
                    >
                      {v.google_email ? (
                        <span
                          title={v.google_name || ""}
                          style={{
                            background: "#E9FFE6",
                            border: "2px solid #2b2b2b",
                            padding: "2px 6px",
                            borderRadius: 6,
                            fontFamily: "monospace",
                          }}
                        >
                          {v.google_email}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-2 px-3 text-xs" data-testid={`admin-users-dob-${v.visitor_id}`}>
                      {v.dob || "—"}
                    </td>
                    <td className="py-2 px-3 text-xs" data-testid={`admin-users-geo-${v.visitor_id}`}>
                      {(() => {
                        const parts = [];
                        [v.city, v.region, v.country_name || v.country].forEach((p) => {
                          const t = (p || "").trim();
                          if (t && !parts.includes(t)) parts.push(t);
                        });
                        return parts.join(", ") || "—";
                      })()}
                    </td>
                    <td className="py-2 px-3 text-xs capitalize" data-testid={`admin-users-device-${v.visitor_id}`}>
                      {v.device || "—"}
                    </td>
                    <td className="py-2 px-3">{(v.first_seen || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 px-3">{(v.last_seen || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 px-3 text-right">{v.visit_count || 0}</td>
                    <td className="py-2 px-3 text-right">{v.total_plays || 0}</td>
                    <td className="py-2 px-3 text-right font-mono text-xs">
                      {(() => {
                        const s = v.playtime_seconds || 0;
                        const m = Math.floor(s / 60);
                        return `${m}m ${(s % 60).toString().padStart(2, "0")}s`;
                      })()}
                    </td>
                    <td className="py-2 px-3 text-right">{(v.coins_earned || 0).toLocaleString()}</td>
                    <td
                      className="py-2 px-3 text-right"
                      data-testid={`admin-users-refs-${v.visitor_id}`}
                    >
                      {v.referrals || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 flex-wrap gap-3">
          <button
            className="btn-3d btn-sm"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - limit))}
            data-testid="admin-users-prev"
          >
            <ChevronsLeft size={16} strokeWidth={3} />
            Prev
          </button>
          <span className="text-sm" style={{ color: "#484848" }} data-testid="admin-users-page">
            Page {page} of {pages}
          </span>
          <button
            className="btn-3d btn-sm"
            disabled={skip + limit >= total}
            onClick={() => setSkip(skip + limit)}
            data-testid="admin-users-next"
          >
            Next
            <ChevronsRight size={16} strokeWidth={3} />
          </button>
        </div>
      </main>
      </div>
    </AdminShell>
  );
};

export default AdminUsers;
